if (!customElements.get('wholesale-recruitment')) {
  class WholesaleRecruitment extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.forms = [...this.querySelectorAll('[data-wholesale-intake-form]')];
      this.interestSelect = this.querySelector('[data-wholesale-interest-select]');
      this.restoreSubmit();
      this.boundClick = this.handleClick.bind(this);
      this.boundSubmit = this.handleSubmit.bind(this);
      this.boundRestore = this.restoreSubmit.bind(this);
      this.boundInvalid = this.handleInvalid.bind(this);
      this.addEventListener('click', this.boundClick);
      this.addEventListener('invalid', this.boundInvalid, true);
      this.forms.forEach((form) => form.addEventListener('submit', this.boundSubmit));
      window.addEventListener('pageshow', this.boundRestore);
      this.querySelectorAll('[data-wholesale-intake-status]:not([hidden])').forEach((status) => status.focus());
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('invalid', this.boundInvalid, true);
      this.forms.forEach((form) => form.removeEventListener('submit', this.boundSubmit));
      window.removeEventListener('pageshow', this.boundRestore);
      this.initialized = false;
    }

    getStickyOffset() {
      const minimum = window.innerWidth < 750 ? 112 : 88;
      if (!document.querySelectorAll) return minimum;
      const candidates = [...document.querySelectorAll('announcement-bar, store-header[data-is-sticky="true"], .cc-announcement, .cc-header, [data-sticky-header]')];
      const rectangles = candidates
        .map((element) => element.getBoundingClientRect?.())
        .filter((rect) => rect && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight)
        .sort((left, right) => left.top - right.top);
      let stackBottom = 0;
      rectangles.forEach((rect) => {
        if (rect.top <= stackBottom + 4) stackBottom = Math.max(stackBottom, rect.bottom);
      });
      return Math.ceil(Math.max(minimum, stackBottom) + 16);
    }

    handleInvalid(event) {
      if (this.invalidTarget) return;
      this.invalidTarget = event.target;
      window.requestAnimationFrame(() => {
        this.invalidTarget.scrollIntoView({ block: 'center', behavior: 'auto' });
        this.invalidTarget = null;
      });
    }

    scrollToTarget(target) {
      if (!target) return;
      const offset = this.getStickyOffset();
      this.style?.setProperty('--wholesale-sticky-offset', `${offset}px`);
      target.style?.setProperty('--wholesale-sticky-offset', `${offset}px`);
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    }

    selectInterest(value) {
      if (!this.interestSelect || !value) return;
      const option = [...this.interestSelect.options].find((item) => item.value === value);
      if (!option) return;
      this.interestSelect.value = value;
      this.interestSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    selectFutureInterest(value) {
      if (!value) return;
      const choice = [...this.querySelectorAll('[data-wholesale-future-choice]')]
        .find((item) => item.dataset.wholesaleFutureChoice === value);
      const input = choice?.querySelector('input[type="checkbox"]');
      if (!input) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-wholesale-scroll]');
      if (!trigger) return;
      const href = trigger.getAttribute('href');
      if (!href || !href.startsWith('#')) return;
      const encodedFragment = href.slice(1);
      if (!encodedFragment) return;
      let fragment;
      try {
        fragment = decodeURIComponent(encodedFragment);
      } catch {
        return;
      }
      if (!fragment) return;
      const target = document.getElementById(fragment);
      if (!target || !this.contains(target)) return;
      event.preventDefault();
      this.selectInterest(trigger.dataset.wholesaleInterest || '');
      this.selectFutureInterest(trigger.dataset.wholesaleFutureInterest || '');
      this.scrollToTarget(target);
    }

    handleSubmit(event) {
      const form = event.currentTarget;
      if (!form) return;
      if (form.dataset.wholesaleSubmitting === 'true') {
        event.preventDefault();
        return;
      }

      this.normalizeBusinessUrl(form);
      const endpoint = form.dataset.wholesaleIntakeEndpoint;
      const canSubmitStructured = endpoint && window.fetch && window.FormData && window.crypto?.randomUUID;
      this.setSubmitting(form, true);
      if (!canSubmitStructured) return;

      event.preventDefault();
      return this.submitStructured(form, endpoint);
    }

    normalizeBusinessUrl(form) {
      const input = form.querySelector('[data-wholesale-business-url]');
      if (!input) return;
      const value = String(input.value || '').trim();
      if (!value) return;
      if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
        input.value = value;
        return;
      }
      const withoutSlashes = value.replace(/^\/\//, '');
      if (/^[^\s@/]+\.[a-z]{2,}(?:[/?#]\S*)?$/i.test(withoutSlashes)) {
        input.value = `https://${withoutSlashes}`;
      } else {
        input.value = value;
      }
    }

    async submitStructured(form, endpoint) {
      // The Hub is the system of record. Decide success or failure here and
      // never hand the form to Shopify's /contact endpoint: Shopify's captcha
      // bootstrap re-submits contact forms on its own schedule, and a native
      // POST after the Hub has already saved produced duplicate rows and an
      // "Email is invalid" error for every applicant (Oct 2026 incident).
      let response = null;
      let result = null;
      try {
        response = await window.fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.buildIntakePayload(form)),
          mode: 'cors',
          credentials: 'omit',
        });
        result = await response.json().catch(() => null);
      } catch {
        response = null;
      }

      if (response && response.ok && result && result.ok) {
        this.completeStructuredSubmit(form);
        return;
      }

      this.setSubmitting(form, false);
      this.showIntakeError(form, response ? response.status : 0, result);
    }

    showIntakeError(form, httpStatus, result) {
      const statusElement = form.querySelector('[data-wholesale-intake-status]');
      if (!statusElement) return;
      const reason = result && typeof result.reason === 'string' ? result.reason : '';
      let message;
      if (httpStatus === 400) {
        message = 'We couldn’t read part of your application. Please check the website field (for example <strong>yourstore.ca</strong>) and the other required fields, then try again.';
      } else if (httpStatus === 409) {
        // The per-page submission id was reused with a different email. A fresh id fixes it.
        delete form.dataset.wholesaleSubmissionId;
        message = 'Your details changed since the first attempt. Please press the button once more.';
      } else if (httpStatus === 429 || reason === 'rate_limited') {
        message = 'Too many attempts from this connection in the last hour. Please try again later, or <a href="/pages/contact">contact us</a> and we’ll set you up by hand.';
      } else {
        message = 'We couldn’t submit your application just now. Your details are still filled in below, so please try again in a minute, or <a href="/pages/contact">contact us</a> and we’ll set you up by hand.';
      }
      statusElement.hidden = false;
      statusElement.classList.remove('wholesale-recruitment__status--success');
      statusElement.classList.add('wholesale-recruitment__status--error');
      statusElement.innerHTML = `<h3>Please try again.</h3><p>${message}</p>`;
      statusElement.focus();
    }

    buildIntakePayload(form) {
      const data = new window.FormData(form);
      const read = (name) => String(data.get(name) || '').trim();
      const optional = (name) => read(name) || undefined;
      if (!form.dataset.wholesaleSubmissionId) {
        form.dataset.wholesaleSubmissionId = window.crypto.randomUUID();
      }
      return {
        schemaVersion: 'wholesale-intake-v1',
        submissionId: form.dataset.wholesaleSubmissionId,
        kind: form.dataset.wholesaleIntakeKind,
        name: read('contact[name]'),
        email: read('contact[email]'),
        businessName: read('contact[business_name]'),
        region: optional('contact[region]'),
        buyerType: optional('contact[buyer_type]'),
        businessUrl: optional('contact[business_url]'),
        primaryInterest: optional('contact[primary_interest]'),
        futureInterests: data.getAll('contact[future_interests]').map((value) => String(value)),
        phone: optional('contact[phone]'),
        note: optional('contact[body]'),
        accessIssue: optional('contact[access_issue]'),
        termsAcknowledged: read('contact[wholesale_terms_acknowledged]') === 'Yes',
        marketingConsent: read('contact[wholesale_marketing_consent]') === 'Yes',
        sourceUrl: window.location.href,
        middleName: read('contact[middle_name]'),
      };
    }

    completeStructuredSubmit(form) {
      const kind = form.dataset.wholesaleIntakeKind;
      const status = form.querySelector('[data-wholesale-intake-status]');
      const content = form.querySelector('[data-wholesale-intake-content]');
      // Do not reset the form: if anything else ever submits it natively, Shopify
      // must receive the real values, not blanks. The fields are hidden below anyway.
      form.dataset.wholesaleCompleted = 'true';
      form.removeAttribute('aria-busy');
      if (content) content.hidden = true;
      if (status) {
        status.hidden = false;
        status.classList.remove?.('wholesale-recruitment__status--error');
        status.classList.add('wholesale-recruitment__status--success');
        status.textContent = kind === 'access_help'
          ? 'Access request received. We’ll verify the business and account connection before sending next steps.'
          : 'Application received. We’ll review your business and email the next steps. Once your access is confirmed, sign in with the email you used here to see your wholesale prices.';
        status.focus();
      }
    }

    setSubmitting(form, submitting) {
      form.dataset.wholesaleSubmitting = submitting ? 'true' : 'false';
      if (submitting) form.setAttribute('aria-busy', 'true');
      else form.removeAttribute('aria-busy');
      const submitButton = form.querySelector('[data-wholesale-submit]');
      if (submitButton) {
        submitButton.disabled = submitting;
        submitButton.textContent = submitting ? submitButton.dataset.submittingLabel : submitButton.dataset.submitLabel;
      }
    }

    restoreSubmit() {
      (this.forms || []).forEach((form) => {
        if (form.dataset.wholesaleCompleted === 'true') return;
        this.setSubmitting(form, false);
      });
    }
  }

  customElements.define('wholesale-recruitment', WholesaleRecruitment);
}
